/**
 * IDE copy for the API's sandbox-state refusals and the guest session cap.
 *
 * The API answers with `{ error, errorKey }`; the sentence in `error` is English
 * only, so the IDE renders its own translatable copy for these keys. The
 * default sentences are the SAME ones the API sends (`sandbox-unavailable.ts`),
 * so a locale without a translation reads exactly like the server string.
 *
 * @module
 */

import { t as translate } from '@molecule/app-i18n'

/** The wording of one `errorKey`. */
interface ErrorCopy {
  key: string
  defaultValue: string
}

/** `errorKey` → i18n key + default sentence. */
export const SANDBOX_ERROR_COPY: Readonly<Record<string, ErrorCopy>> = {
  'sandbox.error.booting': {
    key: 'sandbox.error.booting',
    defaultValue: 'The sandbox is still starting up.',
  },
  'sandbox.error.sleeping': {
    key: 'sandbox.error.sleeping',
    defaultValue: 'This project is asleep. Open it to wake it.',
  },
  'sandbox.error.lost': {
    key: 'sandbox.error.lost',
    defaultValue: 'This project’s sandbox is gone. Start the project again to build a fresh copy.',
  },
  // The lost answer's `messageKey` when Start will restore rather than rebuild
  // (the `errorKey` stays `sandbox.error.lost`).
  'sandbox.error.lost.backup': {
    key: 'sandbox.error.lost.backup',
    defaultValue:
      'This project’s sandbox is gone. Start the project again to restore it from its backup.',
  },
  'sandbox.error.lost.restorePoint': {
    key: 'sandbox.error.lost.restorePoint',
    defaultValue:
      'This project’s sandbox is gone. Start the project again to restore its last saved copy.',
  },
  'guest.limit.sessions': {
    key: 'guest.limit.sessions',
    defaultValue: 'Too many guest sessions. Please sign up for an account.',
  },
  'sandbox.error.concurrency': {
    key: 'sandbox.error.concurrency',
    defaultValue:
      'Your plan’s limit on running projects is reached. Stop another project, or open this one and press Start.',
  },
  // Retryable Start refusals from the project backup (api sandbox/offbox-backup.ts).
  'sandbox.error.backupUnreachable': {
    key: 'sandbox.error.backupUnreachable',
    defaultValue: 'Your project’s backup can’t be reached right now. Try again later.',
  },
  'sandbox.error.backupRestoreFailed': {
    key: 'sandbox.error.backupRestoreFailed',
    defaultValue: 'Restoring your project from its backup failed. Try starting it again.',
  },
  'sandbox.error.backupMissing': {
    key: 'sandbox.error.backupMissing',
    defaultValue:
      'Your project’s backup is missing from storage, so it could not be restored. Try starting again; if it keeps failing, contact support so the project can be recovered.',
  },
  'sandbox.error.backupDamaged': {
    key: 'sandbox.error.backupDamaged',
    defaultValue:
      'Your project’s backup is damaged and cannot be read, so it could not be restored. Try starting again; if it keeps failing, contact support so the project can be recovered.',
  },
  'sandbox.error.backupNoSpace': {
    key: 'sandbox.error.backupNoSpace',
    defaultValue:
      'Your project’s backup does not fit in a new sandbox, so it could not be restored. If it keeps failing, contact support so the project can be recovered.',
  },
  // The import's workspace could not be inspected (api sandbox/import-repo.ts).
  'sandbox.error.workspaceInspect': {
    key: 'sandbox.error.workspaceInspect',
    defaultValue: 'Could not inspect your project’s workspace. Try starting it again.',
  },
  // The start paths' other refusals (api App/Resource/Sandbox/index.ts, sandbox/queue.ts), R95-O-L5.
  'sandbox.error.startFailed': {
    key: 'sandbox.error.startFailed',
    defaultValue: 'The sandbox could not be started. Try again in a moment.',
  },
  'sandbox.error.unreachable': {
    key: 'sandbox.error.unreachable',
    defaultValue: 'The sandbox cannot be reached right now. Try again in a moment.',
  },
  'sandbox.error.egressUnverified': {
    key: 'sandbox.error.egressUnverified',
    defaultValue:
      'New sandboxes are paused while network isolation is re-checked. Please try again shortly.',
  },
  'sandbox.error.timeout': {
    key: 'sandbox.error.timeout',
    defaultValue: 'Sandbox creation timed out. Please try again.',
  },
  // A reserve-held start waited out SANDBOX_RESERVE_WAIT_MS (api sandbox/
  // concurrency.ts BoxStartReserveTimeoutError): retryable, with a retryAfter.
  'sandbox.error.freeingSpace': {
    key: 'sandbox.error.freeingSpace',
    defaultValue: 'The server is still making room for new sandboxes. Try again in a few minutes.',
  },
  'sandbox.error.noProvider': {
    key: 'sandbox.error.noProvider',
    defaultValue: 'Sandboxes are not available on this server. Contact support.',
  },
  'sandbox.error.queueFull': {
    key: 'sandbox.error.queueFull',
    defaultValue: 'All sandbox slots are busy. Please try again in a moment.',
  },
}

/** The `errorKey` of the owner's running-project-limit refusal (429). */
export const SANDBOX_CONCURRENCY_KEY = 'sandbox.error.concurrency'

/**
 * The sentence to show for an API error body: the server's own sentence when
 * it is MORE specific than the IDE copy (a limit refusal that names the
 * projects in the way — `blocking`), else the translatable IDE copy for its
 * `errorKey`, else the server's sentence, else null.
 *
 * @param body - The error body (`error`, `errorKey`, maybe `blocking`).
 * @param t - Translate function (defaults to the global one).
 * @returns The sentence, or null.
 */
export function sandboxErrorSentence(
  body:
    | { error?: unknown; errorKey?: unknown; messageKey?: unknown; blocking?: unknown }
    | null
    | undefined,
  t: Translate = translate,
): string | null {
  const serverSentence = typeof body?.error === 'string' && body.error ? body.error : null
  const errorKey = typeof body?.errorKey === 'string' ? body.errorKey : null
  if (Array.isArray(body?.blocking) && body.blocking.length > 0 && serverSentence)
    return serverSentence
  // A `messageKey` names a more specific sentence for the same `errorKey` (a
  // lost project Start restores instead of rebuilding).
  const messageKey = typeof body?.messageKey === 'string' ? body.messageKey : null
  return sandboxErrorCopy(messageKey, t) ?? sandboxErrorCopy(errorKey, t) ?? serverSentence
}

/** A toast / view action for a known `errorKey`. */
export interface SandboxErrorAction {
  label: string
  href: string
}

/**
 * What the user can DO about a known `errorKey`, or null. The limit refusal
 * says "stop another project" — stopping lives on the dashboard, so the action
 * takes them there.
 *
 * @param errorKey - The API's `errorKey`.
 * @param t - Translate function (defaults to the global one).
 * @returns The action, or null.
 */
export function sandboxErrorAction(
  errorKey: string | null | undefined,
  t: Translate = translate,
): SandboxErrorAction | null {
  if (errorKey !== SANDBOX_CONCURRENCY_KEY) return null
  return {
    label: t('sandbox.error.concurrency.action', undefined, { defaultValue: 'Manage projects' }),
    href: '/dashboard',
  }
}

type Translate = (
  key: string,
  values?: Record<string, string | number | boolean | Date>,
  options?: { defaultValue?: string },
) => string

/**
 * The translatable sentence for a known `errorKey`, or null when the key has no
 * IDE copy (the caller then falls back to the server's `error` string).
 *
 * @param errorKey - The API's `errorKey`.
 * @param t - Translate function (defaults to the global one).
 * @returns The sentence, or null.
 */
export function sandboxErrorCopy(
  errorKey: string | null | undefined,
  t: Translate = translate,
): string | null {
  if (!errorKey || !Object.prototype.hasOwnProperty.call(SANDBOX_ERROR_COPY, errorKey)) return null
  const copy = SANDBOX_ERROR_COPY[errorKey]
  return t(copy.key, undefined, { defaultValue: copy.defaultValue })
}

/**
 * Whether a poller's JSON body says "no sandbox is running, nothing was read"
 * (`sandbox: 'unavailable'`) — as opposed to a real empty answer.
 *
 * @param data - The response body.
 * @returns True when the sandbox was unavailable.
 */
export function isSandboxUnavailableBody(data: unknown): boolean {
  return (data as { sandbox?: unknown } | null | undefined)?.sandbox === 'unavailable'
}
